import React from 'react';
import { FieldControl } from 'react-reactive-form';
import PropTypes from 'prop-types';
import { formatDollars } from '../helpers/number.helper';
import { infoTooltipIcon } from '../assets/superAdminAssets';

const AmountContainer = ({ control, name, label = "", placeholder, disabled = false, isRequired = false, maxlength = 11, setMaxValidation = false, setMinValidation = false, maxValue, minValue, setUpdateFields = '', patternMessage, hideLabel = false, tooltipEnabled, tooltipContent = "", hideIcon = false, showPlaceholderForZero = false, iconType = "input-dollar", onChange = () => { }, allowDecimal = true, verificationLabel, tooltipPosition = "up", tooltipLength = "fit", onClick = () => { }, style, readOnly, labelStyle, className = '', errorMessage = '' }) => (
    <FieldControl
        control={control}
        name={name}
        render={({ handler, touched, hasError, meta, errors }) => {
            const handlerProps = handler();
            handlerProps.value = showPlaceholderForZero && handlerProps.value !== "0.00" ? formatDollars(handlerProps.value, '') : handlerProps?.value;


            const handleChange = (e) => {
                if (disabled) return;

                // Remove all characters except digits, decimal point, and the negative sign at the start
                let rawValue = e.target.value.replace(/[^0-9.-]/g, '');
                if (rawValue === ".") {
                    rawValue = "0."; // Ensure "0." is set when only a dot is entered
                }

                // Ensure the negative sign is only at the beginning
                if (rawValue.includes('-')) {
                    rawValue = `-${rawValue.replace(/-/g, '')}`; // Remove any other negative signs and put it only at the start
                }

                const getFormattedValue = (value) => {
                    // Split the value into integer and decimal parts
                    const parts = value.split('.');

                    // Ensure only one decimal point
                    if (parts.length > 2) {
                        value = `${parts[0]}.${parts[1]}`; // Remove additional decimal points
                    }

                    // Limit decimal places to two digits
                    if (parts[1] && parts[1].length > 2) {
                        value = `${parts[0]}.${parts[1].substring(0, 2)}`;
                    }

                    // Format the integer part with commas, ignoring the negative sign while formatting
                    const integerPart = parts[0].replace(/^-/, ''); // Remove negative sign temporarily
                    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    const formattedValue = value.startsWith('-') ? `-${formattedInteger}` : formattedInteger;

                    // Combine formatted integer and decimal parts
                    const finalFormattedValue = parts.length > 1 ? `${formattedValue}.${parts[1]}` : formattedValue;
                    return finalFormattedValue;
                };

                // handle allowDecimal parameter
                if (!allowDecimal) {
                    rawValue = rawValue?.split('.')[0];
                }

                let finalFormattedValue = getFormattedValue(rawValue);

                // check for maxlength property
                while (finalFormattedValue?.split('.')[0]?.length > maxlength) {
                    // If decimal part exceeds 2 digits, remove the last digit
                    if (finalFormattedValue?.split('.')[1]?.length > 2) {
                        finalFormattedValue = finalFormattedValue.split('.')[0];
                        if (allowDecimal) {
                            finalFormattedValue = finalFormattedValue + '.' + finalFormattedValue.split('.')[1].slice(0, 2)
                        }
                    } else {
                        // Remove the last character if it exceeds maxlength
                        const decimal = (finalFormattedValue?.split('.')[1] ?? '')?.slice(0, 2);
                        finalFormattedValue = getFormattedValue(finalFormattedValue?.split('.')[0].replace(/,/g, "").slice(0, -1)) + ((decimal && allowDecimal) ? `.${decimal}` : '');
                    }
                }

                // Update the input field value
                e.target.value = finalFormattedValue;

                // Call the onChange handler to update the form value
                if (handlerProps.onChange) {
                    if (setMaxValidation && finalFormattedValue.replace(/[^0-9.-]/g, '') > maxValue) {
                        handlerProps.onChange(maxValue.toString());
                        onChange(maxValue.toString());
                    } else if (setMinValidation && finalFormattedValue.replace(/[^0-9.-]/g, '') < minValue) {
                        handlerProps.onChange(minValue.toString());
                        onChange(minValue.toString());
                    } else {
                        handlerProps.onChange(finalFormattedValue || "");
                        onChange(finalFormattedValue || "");
                    }
                }
            };

            const formGroupClass = hideIcon
                ? "form-group"
                : `form-group ${iconType} ${hideLabel ? 'amount-non-label-icon' : ''}`;

            return (
                <div className={`${formGroupClass} ${className}`}>
                    {
                        hideLabel ? null : (
                            <label htmlFor={name} className='verificationLabel' style={labelStyle}>
                                {label}{isRequired && <span className="asterisk">*</span>}
                                {
                                    tooltipEnabled && (
                                        <span className="lm-tooltip ml-1"
                                            data-tooltip={tooltipContent}
                                            data-tooltip-pos={tooltipPosition} data-tooltip-length={tooltipLength}> <img src={infoTooltipIcon} alt="" />
                                        </span>
                                    )
                                }
                                {verificationLabel}
                            </label>
                        )
                    }

                    <input
                        type="text"
                        className={`form-control ${touched && hasError() ? 'is-invalid' : ''}`}
                        id={name}
                        placeholder={placeholder}
                        {...handlerProps}
                        // maxLength={maxlength}
                        disabled={disabled}
                        onChange={handleChange}
                        onClick={onClick}
                        style={style}
                        readOnly={readOnly}
                        autoComplete='false'
                        onBlur={(e) => {
                            if (e.target.value?.trim() == "-") {
                                e.target.value = "0.00";
                                handlerProps.onChange("0.00");
                                onChange("0.00");
                            }
                        }}
                    />

                    {/* Error messages below */}
                    {touched && hasError("negitiveOrZero") && <><span className="error-message">Value should be negitive or 0</span><br /></>}
                    {touched && hasError("positiveNumber") && <><span className="error-message">Enter a number equal to or greater than 0.</span><br /></>}
                    {touched && hasError("isGreaterThanZero") && <><span className="error-message">Value should be greater than 0.</span><br /></>}
                    {touched && hasError("range") && <><span className="error-message">{errors.range.message}</span><br /></>}
                    {touched && hasError("pattern") && <><span className="error-message">Amount should be only numeric values</span><br /></>}
                    {touched && hasError("borrowAmountMaxIssue") && <><span className="error-message">Amount should be less than or equal to 10000000000</span><br /></>}
                    {touched && hasError("required") && <><span className="error-message">{errorMessage || 'Amount is required'}</span><br /></>}
                    {touched && hasError("max") && <><span className="error-message">{errors.max.message}</span><br /></>}
                    {touched && hasError("patternMinMax") && <><span className="error-message">{patternMessage}</span><br /></>}
                </div>
            );
        }}
        meta={{ placeholder: placeholder }}
    />
);

AmountContainer.propTypes = {
    control: PropTypes.object.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    maxlength: PropTypes.number,
    setMaxValidation: PropTypes.bool,
    setMinValidation: PropTypes.bool,
    maxValue: PropTypes.number,
    minValue: PropTypes.number,
    className: PropTypes.string
};

export default AmountContainer;